/**
 * System Configuration Constants
 * Default system settings and configuration for the Uhamiaji SACCOS Management System
 */

// Application Information
export const APP_INFO = {
  NAME: 'Uhamiaji SACCOS Management System',
  SHORT_NAME: 'Uhamiaji SACCOS',
  VERSION: '1.0.0',
  DESCRIPTION: 'A comprehensive savings and credit cooperative society management system',
  AUTHOR: 'Uhamiaji SACCOS Team',
  COPYRIGHT: `© ${new Date().getFullYear()} Uhamiaji SACCOS. All rights reserved.`,
};

// Date and Time Formats
export const DATE_FORMATS = {
  DISPLAY: 'dd MMM yyyy',
  DISPLAY_WITH_TIME: 'dd MMM yyyy HH:mm',
  ISO: 'yyyy-MM-dd',
  ISO_WITH_TIME: 'yyyy-MM-dd\'T\'HH:mm:ss',
  TIME: 'HH:mm',
  MONTH_YEAR: 'MMM yyyy',
  YEAR: 'yyyy',
};

// Currency Configuration
export const CURRENCY = {
  CODE: 'TZS',
  SYMBOL: 'TSh',
  NAME: 'Tanzanian Shilling',
  DECIMAL_PLACES: 2,
  THOUSAND_SEPARATOR: ',',
  DECIMAL_SEPARATOR: '.',
};

// Loan Configuration
export const LOAN_CONFIG = {
  MIN_AMOUNT: 100000,
  MAX_AMOUNT: 50000000,
  MIN_TERM: 1,
  MAX_TERM: 60,
  INTEREST_RATES: {
    FLAT: { MIN: 1, MAX: 25, DEFAULT: 12 },
    REDUCING_BALANCE: { MIN: 1, MAX: 25, DEFAULT: 18 },
  },
  TYPES: [
    { value: 'personal', label: 'Personal Loan' },
    { value: 'business', label: 'Business Loan' },
    { value: 'emergency', label: 'Emergency Loan' },
    { value: 'education', label: 'Education Loan' },
    { value: 'asset', label: 'Asset Acquisition Loan' },
  ],
  STATUS: {
    PENDING: 'pending',
    UNDER_REVIEW: 'under_review',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    DISBURSED: 'disbursed',
    ACTIVE: 'active',
    CLOSED: 'closed',
    DEFAULTED: 'defaulted',
  },
};

// Savings Configuration
export const SAVINGS_CONFIG = {
  MIN_DEPOSIT: 1000,
  MIN_WITHDRAWAL: 1000,
  MIN_BALANCE: 5000,
  WITHDRAWAL_CHARGES: 0,
  ACCOUNT_TYPES: [
    { value: 'compulsory', label: 'Compulsory Savings' },
    { value: 'voluntary', label: 'Voluntary Savings' },
    { value: 'fixed', label: 'Fixed Deposit' },
    { value: 'education', label: 'Education Savings' },
    { value: 'retirement', label: 'Retirement Savings' },
  ],
};

// Shares Configuration
export const SHARES_CONFIG = {
  MIN_SHARES: 1,
  SHARE_VALUE: 10000,
  DIVIDEND_RATE: 0.07,
  TRANSACTION_TYPES: [
    { value: 'purchase', label: 'Share Purchase' },
    { value: 'transfer', label: 'Share Transfer' },
    { value: 'sale', label: 'Share Sale' },
    { value: 'dividend', label: 'Dividend' },
  ],
};

// Member Configuration
export const MEMBER_CONFIG = {
  MEMBERSHIP_TYPES: [
    { value: 'individual', label: 'Individual' },
    { value: 'group', label: 'Group' },
    { value: 'corporate', label: 'Corporate' },
  ],
  STATUS: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    SUSPENDED: 'suspended',
    PENDING: 'pending',
    TERMINATED: 'terminated',
  },
  GENDER: [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ],
  MARITAL_STATUS: [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
  ],
  ID_TYPES: [
    { value: 'national_id', label: 'National ID' },
    { value: 'passport', label: 'Passport' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'voter_id', label: 'Voter ID' },
  ],
};

// File Upload Configuration
export const FILE_CONFIG = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: {
    IMAGES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    DOCUMENTS: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    SPREADSHEETS: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    ALL: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  },
};

// Notification Configuration
export const NOTIFICATION_CONFIG = {
  EMAIL: {
    ENABLED: true,
    FROM: 'noreply@uhamiajisaccos.co.tz',
    FROM_NAME: 'Uhamiaji SACCOS',
  },
  SMS: {
    ENABLED: true,
    SENDER_ID: 'UHAMIAJI',
  },
  IN_APP: {
    ENABLED: true,
    MAX_DISPLAY: 10,
  },
};

// Theme Configuration
export const THEME = {
  COLORS: {
    PRIMARY: '#2563eb',
    SECONDARY: '#64748b',
    SUCCESS: '#10b981',
    WARNING: '#f59e0b',
    ERROR: '#ef4444',
    INFO: '#3b82f6',
  },
};

// Table Configuration
export const TABLE_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'uhamiaji_auth_token',
  REFRESH_TOKEN: 'uhamiaji_refresh_token',
  USER: 'uhamiaji_user',
  THEME: 'uhamiaji_theme',
  LANGUAGE: 'uhamiaji_language',
  SETTINGS: 'uhamiaji_settings',
};

export default {
  APP_INFO,
  DATE_FORMATS,
  CURRENCY,
  LOAN_CONFIG,
  SAVINGS_CONFIG,
  SHARES_CONFIG,
  MEMBER_CONFIG,
  FILE_CONFIG,
  NOTIFICATION_CONFIG,
  THEME,
  TABLE_CONFIG,
  STORAGE_KEYS,
};
